/**
 * Screenshot Capture Component
 *
 * Complete screenshot capture UI with capture button, preview gallery,
 * annotation support, and opt-out controls.
 */

import React, { useState, useCallback } from 'react';
import { useScreenshotCapture, type Screenshot, type CaptureMode } from '../hooks/useScreenshotCapture';
import { ScreenshotAnnotator } from './ScreenshotAnnotator';
import clsx from 'clsx';

interface ScreenshotCaptureProps {
  onScreenshotsChange?: (screenshots: Screenshot[]) => void;
  maxScreenshots?: number;
  className?: string;
}

// Icons
const CameraIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const ExpandIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15 3 21 3 21 9" />
    <polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

const MonitorIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const FileIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export function ScreenshotCapture({
  onScreenshotsChange,
  maxScreenshots = 5,
  className,
}: ScreenshotCaptureProps) {
  const {
    screenshots,
    activeScreenshot,
    isCapturing,
    error,
    captureViewport,
    captureFullPage,
    deleteScreenshot,
    clearScreenshots,
    setActiveScreenshot,
    addAnnotation,
    removeAnnotation,
    flattenAnnotations,
    canCapture,
    clearError,
    getTotalSize,
  } = useScreenshotCapture({ maxScreenshots });

  const [showCaptureOptions, setShowCaptureOptions] = useState(false);
  const [editingScreenshot, setEditingScreenshot] = useState<Screenshot | null>(null);
  const [previewScreenshot, setPreviewScreenshot] = useState<Screenshot | null>(null);
  const [optOut, setOptOut] = useState(false);

  // Notify parent when screenshots change (skip initial empty mount to avoid re-render loop)
  const onChangeRef = React.useRef(onScreenshotsChange);
  onChangeRef.current = onScreenshotsChange;
  const prevLenRef = React.useRef(screenshots.length);
  React.useEffect(() => {
    // Only notify parent when screenshots actually change (not on initial empty mount)
    if (screenshots.length === 0 && prevLenRef.current === 0) return;
    prevLenRef.current = screenshots.length;
    if (onChangeRef.current && !optOut) {
      onChangeRef.current(screenshots);
    }
  }, [screenshots, optOut]);

  /**
   * Handle capture with mode selection
   */
  const handleCapture = useCallback(
    async (mode: CaptureMode) => {
      setShowCaptureOptions(false);

      if (mode === 'viewport') {
        await captureViewport();
      } else if (mode === 'full_page') {
        await captureFullPage();
      }
    },
    [captureViewport, captureFullPage]
  );

  /**
   * Handle screenshot annotation
   */
  const handleEdit = useCallback((screenshot: Screenshot) => {
    setEditingScreenshot(screenshot);
  }, []);

  /**
   * Handle annotation save
   */
  const handleSaveAnnotation = useCallback(
    (flattenedDataUrl: string) => {
      if (editingScreenshot) {
        flattenAnnotations(editingScreenshot.id, flattenedDataUrl);
        setEditingScreenshot(null);
      }
    },
    [editingScreenshot, flattenAnnotations]
  );

  /**
   * Handle delete screenshot
   */
  const handleDelete = useCallback(
    (id: string) => {
      deleteScreenshot(id);
      if (previewScreenshot?.id === id) {
        setPreviewScreenshot(null);
      }
    },
    [deleteScreenshot, previewScreenshot]
  );

  /**
   * Format file size for display
   */
  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // If opted out, show minimal UI
  if (optOut) {
    return (
      <div className={clsx('psc-screenshot', className)}>
        <div className="psc-screenshot__opt-out">
          <label className="psc-screenshot__opt-out-label">
            <input
              type="checkbox"
              checked={optOut}
              onChange={(e) => setOptOut(e.target.checked)}
              className="psc-screenshot__opt-out-checkbox"
            />
            Don't include screenshots
          </label>
        </div>
      </div>
    );
  }

  return (
    <div className={clsx('psc-screenshot', className)}>
      {/* Header with capture button */}
      <div className="psc-screenshot__header">
        <h4 className="psc-screenshot__title">
          <CameraIcon /> Screenshots ({screenshots.length}/{maxScreenshots})
        </h4>

        <div className="psc-screenshot__header-actions">
          <label className="psc-screenshot__opt-out-label">
            <input
              type="checkbox"
              checked={optOut}
              onChange={(e) => {
                setOptOut(e.target.checked);
                if (e.target.checked) {
                  clearScreenshots();
                }
              }}
              className="psc-screenshot__opt-out-checkbox"
            />
            Opt out
          </label>

          <div className="psc-screenshot__capture-wrapper">
            <button
              className="psc-button psc-button--secondary psc-button--sm"
              onClick={() => setShowCaptureOptions(!showCaptureOptions)}
              disabled={!canCapture() || isCapturing}
            >
              {isCapturing ? 'Capturing...' : 'Capture Screenshot'}
            </button>

            {showCaptureOptions && (
              <div className="psc-screenshot__capture-options">
                <button
                  className="psc-screenshot__capture-option"
                  onClick={() => handleCapture('viewport')}
                >
                  <MonitorIcon />
                  <span>Visible Viewport</span>
                  <small>Capture what's currently visible</small>
                </button>
                <button
                  className="psc-screenshot__capture-option"
                  onClick={() => handleCapture('full_page')}
                >
                  <FileIcon />
                  <span>Full Page</span>
                  <small>Capture entire scrollable page</small>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="psc-screenshot__error" role="alert">
          <span>{error}</span>
          <button onClick={clearError} aria-label="Dismiss error">
            <CloseIcon />
          </button>
        </div>
      )}

      {/* Screenshot gallery */}
      {screenshots.length > 0 && (
        <div className="psc-screenshot__gallery">
          {screenshots.map((screenshot) => (
            <div key={screenshot.id} className="psc-screenshot__item">
              <div className="psc-screenshot__thumbnail-wrapper">
                <img
                  src={screenshot.dataUrl}
                  alt={`Screenshot ${screenshot.id}`}
                  className="psc-screenshot__thumbnail"
                  onClick={() => setPreviewScreenshot(screenshot)}
                />
                <div className="psc-screenshot__overlay">
                  <button
                    className="psc-screenshot__overlay-btn"
                    onClick={() => setPreviewScreenshot(screenshot)}
                    title="Preview"
                  >
                    <ExpandIcon />
                  </button>
                  <button
                    className="psc-screenshot__overlay-btn"
                    onClick={() => handleEdit(screenshot)}
                    title="Add annotations"
                  >
                    <EditIcon />
                  </button>
                  <button
                    className="psc-screenshot__overlay-btn psc-screenshot__overlay-btn--danger"
                    onClick={() => handleDelete(screenshot.id)}
                    title="Delete"
                  >
                    <TrashIcon />
                  </button>
                </div>
              </div>
              <div className="psc-screenshot__info">
                <span className="psc-screenshot__mode">
                  {screenshot.captureMode === 'full_page' ? 'Full Page' : 'Viewport'}
                </span>
                <span className="psc-screenshot__size">
                  {screenshot.width}x{screenshot.height}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {screenshots.length === 0 && !isCapturing && (
        <div className="psc-screenshot__empty">
          <CameraIcon />
          <p>No screenshots captured</p>
          <small>Click "Capture Screenshot" to add visual context</small>
        </div>
      )}

      {/* Size info */}
      {screenshots.length > 0 && (
        <div className="psc-screenshot__size-info">
          Total size: {formatSize(getTotalSize())}
        </div>
      )}

      {/* Preview modal */}
      {previewScreenshot && (
        <div
          className="psc-screenshot__preview-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget) setPreviewScreenshot(null);
          }}
        >
          <div className="psc-screenshot__preview-content">
            <button
              className="psc-screenshot__preview-close"
              onClick={() => setPreviewScreenshot(null)}
            >
              <CloseIcon />
            </button>
            <img
              src={previewScreenshot.dataUrl}
              alt="Screenshot preview"
              className="psc-screenshot__preview-image"
            />
            <div className="psc-screenshot__preview-actions">
              <button
                className="psc-button psc-button--secondary"
                onClick={() => {
                  setPreviewScreenshot(null);
                  handleEdit(previewScreenshot);
                }}
              >
                <EditIcon /> Edit Annotations
              </button>
              <button
                className="psc-button psc-button--secondary psc-button--danger"
                onClick={() => handleDelete(previewScreenshot.id)}
              >
                <TrashIcon /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Annotation modal */}
      {editingScreenshot && (
        <div className="psc-screenshot__annotator-modal">
          <ScreenshotAnnotator
            screenshot={editingScreenshot}
            onAnnotationAdd={(annotation) => addAnnotation(editingScreenshot.id, annotation)}
            onAnnotationRemove={(id) => removeAnnotation(editingScreenshot.id, id)}
            onSave={handleSaveAnnotation}
            onCancel={() => setEditingScreenshot(null)}
          />
        </div>
      )}
    </div>
  );
}
